import React from "react";
import {
  AgentLocatorProvider,
  BaggageInfoEnum,
  type BookedFlightItinerary,
  type AirBookingFlightItinerarySlice,
  PenaltiesInfoEnum,
  type Penalty,
  PenaltyEnum,
} from "@b2bportal/air-booking-api";
import { type Restriction, RestrictionSymbol } from "@b2bportal/core-flights";
import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import { CoreFlightsComponent } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { FlightItineraryRestrictionInfo } from "@tenant/flights";
import clsx from "clsx";
import defaultStyles from "./FlightItineraryRestrictionsDetails.module.scss";

type TranslateFn = ReturnType<typeof useI18nContext>["t"];
type FormatFiatCurrencyFn = ReturnType<
  typeof useI18nContext
>["formatFiatCurrency"];

interface FlightItineraryRestrictionsDetailsProps {
  itinerary: BookedFlightItinerary;
  slice: AirBookingFlightItinerarySlice;
  className?: string;
}

export const FlightItineraryRestrictionsDetails = ({
  itinerary,
  slice,
  className,
}: FlightItineraryRestrictionsDetailsProps) => {
  const { t, formatFiatCurrency } = useI18nContext();

  const COMPONENT_KEY = CoreFlightsComponent.FlightItineraryRestrictionsDetails;
  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const restrictions = React.useMemo(
    () => collectRestrictions(itinerary, slice, t, formatFiatCurrency),
    [t, formatFiatCurrency, itinerary, slice]
  );

  return (
    <div className={clsx(block, className)}>
      {restrictions.map((restriction) => (
        <FlightItineraryRestrictionInfo
          key={`${restriction.name}-${restriction.description}`}
          restriction={restriction}
        />
      ))}
    </div>
  );
};

const BaggageNotAllowed = {
  BaggageInfo: BaggageInfoEnum.NotAllowed,
};
const collectBaggageRestrictions = (
  itinerary: BookedFlightItinerary,
  slice: AirBookingFlightItinerarySlice,
  t: TranslateFn,
  formatCurrency: FormatFiatCurrencyFn
): Restriction[] => {
  const sliceBaggageInfo = slice.restrictions?.baggageInfo ?? BaggageNotAllowed;
  const baggageInfo =
    sliceBaggageInfo.BaggageInfo !== BaggageInfoEnum.NotAllowed
      ? sliceBaggageInfo
      : (itinerary.restrictions?.baggageInfo ?? BaggageNotAllowed);

  switch (baggageInfo.BaggageInfo) {
    case BaggageInfoEnum.NotAllowed:
      return [
        {
          name: t("restrictions.baggage.checkedBagAllowance"),
          symbol: RestrictionSymbol.Unavailable,
          description: t("restrictions.baggage.notAllowed"),
        },
      ];
    case BaggageInfoEnum.Allowed:
      return [
        ...(baggageInfo.allowance.descriptions.length > 0
          ? [
              {
                name: "",
                description: baggageInfo.allowance.descriptions.join(" "),
                symbol: RestrictionSymbol.Included,
              },
            ]
          : []),
        ...(baggageInfo.charges.length > 0
          ? [
              {
                name: t("restrictions.baggage.checkedBagAllowance"),
                symbol: RestrictionSymbol.Purchasable,
                description: baggageInfo.charges
                  .map((charge) =>
                    charge.maximumMass != null
                      ? t("restrictions.baggage.bagChargeWithMass", {
                          bagPiece: charge.firstPiece,
                          bagPrice: formatCurrency({
                            value: charge.amount,
                            currencyCode: charge.currency,
                          }),
                          mass: `${charge.maximumMass.value}${charge.maximumMass.unit}`,
                        })
                      : t("restrictions.baggage.bagCharge", {
                          bagPiece: charge.firstPiece,
                          bagPrice: formatCurrency({
                            value: charge.amount,
                            currencyCode: charge.currency,
                          }),
                        })
                  )
                  .join(", "),
              },
            ]
          : []),
        ...(baggageInfo.charges.length < 1 && baggageInfo.allowance.pieces > 0
          ? [
              {
                name: t("restrictions.baggage.checkedBagAllowance"),
                symbol: RestrictionSymbol.Included,
                description:
                  baggageInfo.allowance.maximumMass != null
                    ? t("restrictions.baggage.bagsIncludedWithMass", {
                        count: baggageInfo.allowance.pieces,
                        mass: `${baggageInfo.allowance.maximumMass.value}${baggageInfo.allowance.maximumMass.unit}`,
                      })
                    : t("restrictions.baggage.bagsIncluded", {
                        count: baggageInfo.allowance.pieces,
                      }),
              },
            ]
          : []),
      ];
    case BaggageInfoEnum.Unknown:
      return [];
  }
};

enum PenaltyType {
  Cancellation,
  Exchange,
}

const getPenaltyName = (type: PenaltyType, t: TranslateFn): string => {
  switch (type) {
    case PenaltyType.Cancellation:
      return t("restrictions.cancellations.name");
    case PenaltyType.Exchange:
      return t("restrictions.exchanges.name");
  }
};

const getPenaltyInfoInternal = (
  penalty: Penalty,
  t: TranslateFn,
  formatCurrency: FormatFiatCurrencyFn
): Omit<Restriction, "name"> => {
  switch (penalty.Penalty) {
    case PenaltyEnum.WithFee:
      return {
        description: t("restrictions.penalties.withFee", {
          price: formatCurrency({
            value: penalty.amount,
            currencyCode: penalty.currency,
          }),
        }),
        symbol: RestrictionSymbol.Purchasable,
      };
    case PenaltyEnum.Unknown:
      return {
        description: t("restrictions.penalties.contactSupport"),
        symbol: RestrictionSymbol.Unavailable,
      };
    case PenaltyEnum.NotPossible:
      return {
        description: t("restrictions.penalties.nonRefundable"),
        symbol: RestrictionSymbol.Unavailable,
      };
    case PenaltyEnum.NoPenalty:
      return {
        description: t("restrictions.penalties.free"),
        symbol: RestrictionSymbol.Included,
      };
  }
};

const getPenaltyInfo = (
  penalty: Penalty,
  type: PenaltyType,
  brandName: string | undefined,
  t: TranslateFn,
  formatCurrency: FormatFiatCurrencyFn
): Restriction => {
  const name = getPenaltyName(type, t);
  // TODO: It's probably not the best idea to be checking for JetBlue brands here.
  // In the long run we should consider changing the API so this can come from the backend.
  switch (brandName) {
    case "Blue":
    case "Blue Plus":
    case "Blue Extra":
    case "Mint":
      return {
        name,
        description: (() => {
          switch (type) {
            case PenaltyType.Cancellation:
              return t("restrictions.penalties.cancelForCredit");
            case PenaltyType.Exchange:
              return t("restrictions.penalties.free");
          }
        })(),
        symbol: RestrictionSymbol.Included,
      };
    case "Blue Ref":
    case "Blue Plus Ref":
    case "Blue Extra Ref":
    case "Mint Ref":
      return {
        name,
        description: t("restrictions.penalties.free"),
        symbol: RestrictionSymbol.Included,
      };
    default:
      return { name, ...getPenaltyInfoInternal(penalty, t, formatCurrency) };
  }
};

const PenaltiesUnknown = { PenaltiesInfo: PenaltiesInfoEnum.Unknown };
const collectPenaltyRestrictions = (
  itinerary: BookedFlightItinerary,
  slice: AirBookingFlightItinerarySlice,
  t: TranslateFn,
  formatCurrency: FormatFiatCurrencyFn
): Restriction[] => {
  const slicePenaltiesInfo =
    slice.restrictions?.penaltiesInfo ?? PenaltiesUnknown;
  const penaltiesInfo =
    slicePenaltiesInfo.PenaltiesInfo !== PenaltiesInfoEnum.Unknown
      ? slicePenaltiesInfo
      : (itinerary.restrictions?.penaltiesInfo ?? PenaltiesUnknown);

  switch (penaltiesInfo.PenaltiesInfo) {
    case PenaltiesInfoEnum.ExternallyManaged:
    case PenaltiesInfoEnum.Unknown: {
      const provider = itinerary.travelItinerary.locators?.agent.provider;
      return [
        {
          name: getPenaltyName(PenaltyType.Cancellation, t),
          symbol: RestrictionSymbol.Unknown,
          // TODO: CapOne has special handling for TF here. Do we need to do that too?
          description:
            provider === AgentLocatorProvider.travelfusion
              ? t("restrictions.penalties.viewEligibility")
              : t("restrictions.penalties.contactAirline"),
        },
      ];
    }
    case PenaltiesInfoEnum.Known:
      return [
        getPenaltyInfo(
          penaltiesInfo.cancelPenalty,
          PenaltyType.Cancellation,
          slice.fareShelf?.brandName,
          t,
          formatCurrency
        ),
        getPenaltyInfo(
          penaltiesInfo.changePenalty,
          PenaltyType.Exchange,
          slice.fareShelf?.brandName,
          t,
          formatCurrency
        ),
      ];
  }
};

const collectRestrictions = (
  itinerary: BookedFlightItinerary,
  slice: AirBookingFlightItinerarySlice,
  t: TranslateFn,
  formatCurrency: FormatFiatCurrencyFn
): Restriction[] => {
  return [
    ...collectBaggageRestrictions(itinerary, slice, t, formatCurrency),
    ...collectPenaltyRestrictions(itinerary, slice, t, formatCurrency),
  ];
};
